import infoIcon from "@assets/images/info.svg";
import minusCircle from "@assets/images/minus-circle.svg";
import plusIcon from "@assets/images/plus-teal.svg";
import saveIconWhite from "@assets/images/save-icon-white.svg";
import xWhite from "@assets/images/x-white.svg";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField, {
  Choice
} from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  SearchMaterialBy,
  useAddCustomerMaterialsMutation,
  useGetSaleOrgAndDistChannelBySoldToQuery,
  useSearchSuggestionMaterialLazyQuery,
  useSearchSuggestionSoldTosCustMatLazyQuery
} from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

import DialogWaitingConfirm from "../DialogWaitingConfirm";
import useStyles from "./styles";

interface DialogAddMaterial {
  onConfirm: (data: any) => void;
  onClose: () => void;
  open: boolean;
}

const DialogAddMaterial: React.FC<DialogAddMaterial> = ({
  onConfirm,
  onClose,
  open
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [salesOrganizationOptions, setSalesOrganizationOptions] = useState<
    Choice[]
  >([]);
  const [distributionChannelOptions, setDistributionChannelOptions] = useState<
    Choice[]
  >([]);
  const [openProcessDialog, setOpenProcessDialog] = useState<boolean>(false);
  const [itemErrors, setItemErrors] = useState<any[]>([]);
  const [materialSuggestions, setMaterialSuggestions] = useState<{
    [key: string]: Array<{ title: string; value: string }>;
  }>({});
  const [currentSearchItemId, setCurrentSearchItemId] = useState<string | null>(
    null
  );
  const notify = useNotifier();

  const [
    getDataSoldTo,
    { data: fetchDataSoldTo, loading: loadingDataSoldTo }
  ] = useSearchSuggestionSoldTosCustMatLazyQuery();

  const [addCustomerMaterials] = useAddCustomerMaterialsMutation({
    onCompleted: data => {
      if (
        data.addCustomerMaterials.itemErrors &&
        data.addCustomerMaterials.itemErrors.some(
          error =>
            error.materialCode === true || error.soldToMaterialCode === true
        )
      ) {
        setItemErrors(data.addCustomerMaterials.itemErrors);
        formik.validateForm();
      } else {
        onConfirm(data.addCustomerMaterials.items);
      }
    },
    onError: error => {
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const textError = intl.formatMessage({
    defaultMessage: "โปรดระบุข้อมูล"
  });

  const MaterialFilterSchema = useMemo(
    () =>
      Yup.object().shape({
        soldTo: Yup.string().required(textError),
        saleOrganization: Yup.string().required(textError),
        distributionChannel: Yup.string().required(textError),
        items: Yup.array()
          .of(
            Yup.object().shape({
              soldToMaterialCode: Yup.string()
                .required(textError)
                .test(
                  "duplicate-check",
                  intl.formatMessage({
                    defaultMessage: "Duplicate Customer Material"
                  }),
                  function() {
                    if (!itemErrors || itemErrors.length === 0) {
                      return true;
                    }

                    const { path } = this;
                    const itemIndex = parseInt(
                      path.split("[")[1].split("]")[0],
                      10
                    );
                    const currentError = itemErrors[itemIndex];

                    if (currentError?.soldToMaterialCode === true) {
                      return false;
                    }
                    return true;
                  }
                ),
              materialCode: Yup.string()
                .required(textError)
                .test(
                  "duplicate-check",
                  intl.formatMessage({
                    defaultMessage: "Duplicate Material Code"
                  }),
                  function() {
                    if (!itemErrors || itemErrors.length === 0) {
                      return true;
                    }

                    const { path } = this;
                    const itemIndex = parseInt(
                      path.split("[")[1].split("]")[0],
                      10
                    );
                    const currentError = itemErrors[itemIndex];

                    if (currentError?.materialCode === true) {
                      return false;
                    }
                    return true;
                  }
                )
            })
          )
          .min(1, textError)
      }),
    [textError, intl, itemErrors]
  );

  const formik = useFormik({
    initialValues: {
      soldTo: "",
      saleOrganization: "",
      distributionChannel: "",
      items: [
        {
          id: `item-${Date.now()}-0`,
          soldToMaterialCode: "",
          materialCode: ""
        }
      ]
    },
    validationSchema: MaterialFilterSchema,
    onSubmit: () => {
      //
    }
  });

  const onSubmitForm = async () => {
    try {
      setOpenProcessDialog(true);
      formik.setTouched({
        soldTo: true,
        saleOrganization: true,
        distributionChannel: true,
        items: formik.values.items.map(() => ({
          soldToMaterialCode: true,
          materialCode: true
        }))
      });
      await formik.validateForm();
      if (formik.isValid) {
        await addCustomerMaterials({
          variables: {
            soldTo: formik.values.soldTo,
            saleOrg: formik.values.saleOrganization,
            distribution: formik.values.distributionChannel,
            items: formik.values.items.map(e => ({
              materialCode: e.materialCode,
              soldToMaterialCode: e.soldToMaterialCode
            }))
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setOpenProcessDialog(false);
    }
  };

  useEffect(() => {
    if (open) {
      formik.resetForm();
      setItemErrors([]);
    }
  }, [open]);

  const dataSoldTo = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSoldTo?.searchSuggestionSoldTosCustMat) || [];
    return data.map(data => ({
      title: data.domesticSoldToSearchFilterDisplayText,
      value: data.code
    }));
  }, [fetchDataSoldTo?.searchSuggestionSoldTosCustMat]);

  const handleChangeSoldToKeyword = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSoldTo({
        variables: { first: 100, filter: { search: value } }
      });
    }
  };

  const getValueFromOptionTitle = title =>
    dataSoldTo.find(x => x.title === title)?.value;

  const setDistributionChannelDefaultValue = (options: Choice[]) => {
    const defaultCode: string = "10";
    const defaultItem = options.find(option => option.value === defaultCode);
    if (defaultItem) {
      formik.setFieldValue("distributionChannel", defaultItem.value);
    }
  };

  const {
    data: dataSaleOrganization
  } = useGetSaleOrgAndDistChannelBySoldToQuery({
    variables: {
      soldTo: formik?.values?.soldTo || ""
    },
    onCompleted: data => {
      const saleOrganizationChoices =
        data?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization || [];
      const distributionChannels =
        data?.getSaleOrgAndDistChannelBySoldTo?.distributionChannel || [];

      if (saleOrganizationChoices.length > 0) {
        const salesOrganizationOptions: Choice[] = saleOrganizationChoices.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setSalesOrganizationOptions(salesOrganizationOptions);
      }
      if (distributionChannels.length > 0) {
        const distributionChannelOptions: Choice[] = distributionChannels.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setDistributionChannelOptions(distributionChannelOptions);
        setDistributionChannelDefaultValue(distributionChannelOptions);
      }
    }
  });

  const handleChangeSoldTo = (value: string) => {
    if (!value || value === "") {
      formik.setFieldValue("soldTo", "");
      formik.setFieldValue("saleOrganization", "");
      formik.setFieldValue("distributionChannel", "");
      setSalesOrganizationOptions([]);
      setDistributionChannelOptions([]);
    } else {
      formik.setFieldValue("soldTo", getValueFromOptionTitle(value));
      formik.setFieldValue("saleOrganization", "");
      formik.setFieldValue("distributionChannel", "");
    }
  };

  const handleChangeSaleOrganization = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("saleOrganization", selectedValue);

    if (selectedValue) {
      const distribution = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrgByDistChannel.find(
        org => org.salesOrg === selectedValue
      );
      if (distribution) {
        const formattedData = distribution.distributionChannel.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setDistributionChannelOptions(formattedData);
      } else {
        setDistributionChannelOptions([]);
      }
    } else {
      const distributionChannels =
        dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo
          ?.distributionChannel || [];
      const formattedData = distributionChannels.map(item => ({
        value: item.code,
        label: item.displayText
      }));
      setDistributionChannelOptions(formattedData);
    }
  };

  const handleChangeDistributionChannels = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("distributionChannel", selectedValue);

    if (selectedValue) {
      const salesOrgData = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.distChannelBySalesOrg.find(
        org => org.distributionChannel === selectedValue
      );

      if (salesOrgData) {
        const formattedData = salesOrgData.salesOrg.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setSalesOrganizationOptions(formattedData);
      } else {
        setSalesOrganizationOptions([]);
      }
    } else {
      const data = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization?.map(
        item => ({
          value: item.code,
          label: item.displayText
        })
      );
      setSalesOrganizationOptions(data || []);
    }
  };

  const isDeleteItem = useMemo(() => formik.values.items.length > 1, [
    formik.values.items
  ]);

  const isFormDisabled = useMemo(() => {
    const hasRequiredFields =
      formik.values.soldTo &&
      formik.values.saleOrganization &&
      formik.values.distributionChannel;

    const hasValidItems = formik.values.items.some(
      item => item.soldToMaterialCode && item.materialCode
    );

    return hasRequiredFields && hasValidItems;
  }, [
    formik.values.soldTo,
    formik.values.saleOrganization,
    formik.values.distributionChannel,
    formik.values.items
  ]);

  const handleCloseDialog = () => {
    formik.resetForm();
    onClose();
  };

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionMaterialLazyQuery({ fetchPolicy: "no-cache" });

  const dataMatCode = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSuggestions?.searchSuggestionMaterial) || [];
    return data.map(data => ({
      title: `${data.displayText}`,
      value: data.materialCode
    }));
  }, [fetchDataSuggestions?.searchSuggestionMaterial]);

  const handleChangeMatCodeKeyword = (value: string, itemId: string) => {
    if (value && value.length >= 2) {
      setCurrentSearchItemId(itemId);
      getDataSuggestions({
        variables: {
          first: 100,
          filter: { matCode: value },
          saleOrg: formik.values.saleOrganization || "",
          distributionChannel: formik.values.distributionChannel || "",
          searchBy: SearchMaterialBy.MAT_CODE,
          status: "active"
        }
      });
    }
  };

  useEffect(() => {
    if (fetchDataSuggestions && currentSearchItemId !== null) {
      const suggestions =
        mapEdgesToItems(fetchDataSuggestions?.searchSuggestionMaterial) || [];
      setMaterialSuggestions(prev => ({
        ...prev,
        [currentSearchItemId]: suggestions.map(item => ({
          title: item.displayText,
          value: item.materialCode
        }))
      }));
    }
  }, [fetchDataSuggestions, currentSearchItemId]);

  const handleChangeMatCodeForIndex = useCallback(
    (value: string, index: number) => {
      if (!value || value === "") {
        formik.setFieldValue(`items[${index}].materialCode`, "");
        if (itemErrors.length > 0) {
          const newItemErrors = [...itemErrors];
          if (newItemErrors[index]) {
            newItemErrors[index] = {
              ...newItemErrors[index],
              materialCode: false
            };
            setItemErrors(newItemErrors);
            setTimeout(() => {
              formik.validateForm();
            }, 0);
          }
        }
      } else {
        const matchedOption = dataMatCode.find(x => x.title === value);
        if (matchedOption) {
          const currentValue = formik.values.items[index]?.materialCode;
          if (currentValue !== matchedOption.value) {
            formik.setFieldValue(
              `items[${index}].materialCode`,
              matchedOption.value
            );
            if (itemErrors.length > 0) {
              const newItemErrors = [...itemErrors];
              if (newItemErrors[index]) {
                newItemErrors[index] = {
                  ...newItemErrors[index],
                  materialCode: false
                };
                setItemErrors(newItemErrors);
                setTimeout(() => {
                  formik.validateForm();
                }, 0);
              }
            }
          }
        }
      }
    },
    [formik, dataMatCode, itemErrors, setItemErrors]
  );

  const handleChangeSoldToMaterialCode = useCallback(
    (value: string, index: number) => {
      formik.setFieldValue(`items[${index}].soldToMaterialCode`, value.trim());
      if (itemErrors.length > 0) {
        const newItemErrors = [...itemErrors];
        if (newItemErrors[index]) {
          newItemErrors[index] = {
            ...newItemErrors[index],
            soldToMaterialCode: false,
            materialCode: false
          };
          setItemErrors(newItemErrors);
          setTimeout(() => {
            formik.validateForm();
          }, 0);
        }
      }
    },
    [formik, itemErrors, setItemErrors]
  );

  const addItem = useCallback(() => {
    const newItems = [
      ...formik.values.items,
      {
        id: `item-${Date.now()}-${formik.values.items.length}`,
        soldToMaterialCode: "",
        materialCode: ""
      }
    ];

    formik.setFieldValue("items", newItems);

    // Keep existing touched state, only add new row as false
    const currentTouched = formik.touched.items || [];
    formik.setTouched({
      ...formik.touched,
      items: [
        ...currentTouched,
        {
          soldToMaterialCode: false,
          materialCode: false
        }
      ]
    });

    // Add empty error object for new row
    if (itemErrors.length > 0) {
      setItemErrors([...itemErrors, {}]);
    }
  }, [formik, itemErrors]);

  const removeItem = useCallback(
    (index: number) => {
      const newItems = formik.values.items.filter((_, i) => i !== index);
      formik.setFieldValue("items", newItems);

      // Remove touched state of deleted row (keep existing ones)
      const newTouched =
        formik.touched.items?.filter((_, i) => i !== index) || [];
      formik.setTouched({
        ...formik.touched,
        items: newTouched
      });

      // Update itemErrors by removing error at deleted index
      if (itemErrors.length > 0) {
        const newItemErrors = itemErrors.filter((_, i) => i !== index);
        setItemErrors(newItemErrors);
        setTimeout(() => {
          formik.validateForm();
        }, 0);
      }
    },
    [formik, itemErrors]
  );

  return (
    <Dialog open={open} className={classes.root} fullWidth maxWidth="md">
      <DialogTitle className={classes.dialogTitle}>
        <MyTypography variant="h6" className={classes.titleHeader}>
          <span>
            <strong>Add</strong> Customer Material
          </span>
          <InlineSVG
            src={xWhite}
            width={24}
            height={24}
            onClick={handleCloseDialog}
            className={classes.closeIcon}
          />
        </MyTypography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} className={classes.detailsContainer}>
          <Grid item xs={12}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>Sold-to Party</MyTypography>
              <span className={classes.required}>*</span>
              <span>&nbsp;</span>
              <span> (เลือกได้เพียง 1 รายการ)</span>
            </div>
            <Autocomplete
              resetAlways
              optionsProp={dataSoldTo}
              placeholderProps={intl.formatMessage({
                defaultMessage: "ค้นหา Sold-to Code/Name",
                description: "label"
              })}
              onChangeProps={handleChangeSoldToKeyword}
              onSearch={handleChangeSoldTo}
              loading={loadingDataSoldTo}
              id="soldTo"
              name="soldTo"
              error={formik.touched.soldTo && Boolean(formik.errors.soldTo)}
            />
            {formik.touched.soldTo && formik.errors.soldTo && (
              <MyTypography variant="caption" className={classes.validateError}>
                {formik.errors.soldTo}
              </MyTypography>
            )}
          </Grid>
          <Grid item xs={6}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>
                Sales Organization
              </MyTypography>
              <span className={classes.required}>*</span>
            </div>
            <SingleSelectField
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              testId="saleOrganization"
              name={"saleOrganization"}
              id="saleOrganization"
              value={formik?.values?.saleOrganization}
              choices={salesOrganizationOptions || []}
              onChange={handleChangeSaleOrganization}
              placeholder={"เลือก Sales Organization"}
              className={classes.selectField}
              error={
                formik.touched.saleOrganization &&
                Boolean(formik.errors.saleOrganization)
              }
            />
            {formik.touched.saleOrganization && formik.errors.saleOrganization && (
              <MyTypography variant="caption" className={classes.validateError}>
                {formik.errors.saleOrganization}
              </MyTypography>
            )}
          </Grid>
          <Grid item xs={6}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>
                Distribution Channel
              </MyTypography>
              <span className={classes.required}>*</span>
            </div>
            <SingleSelectField
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              testId="distributionChannel"
              name={"distributionChannel"}
              id="distributionChannel"
              value={formik?.values?.distributionChannel}
              onChange={handleChangeDistributionChannels}
              placeholder={"เลือก Distribution Channel"}
              choices={distributionChannelOptions || []}
              className={classes.selectField}
              error={
                formik.touched.distributionChannel &&
                Boolean(formik.errors.distributionChannel)
              }
            />
            {formik.touched.distributionChannel &&
              formik.errors.distributionChannel && (
                <MyTypography
                  variant="caption"
                  className={classes.validateError}
                >
                  {formik.errors.distributionChannel}
                </MyTypography>
              )}
          </Grid>
          <Grid item xs={12}>
            <div className={classes.inputTitleBlock}>
              <Grid item xs={5}>
                <MyTypography variant={"subtitle3"}>
                  Customer Material
                  <span className={classes.required}>*</span>
                </MyTypography>
              </Grid>
              <Grid item xs={7}>
                <MyTypography variant={"subtitle3"}>
                  Material Code / Description
                  <span className={classes.required}>*</span>
                </MyTypography>
              </Grid>
            </div>
          </Grid>
          <Grid item xs={12} className={classes.itemsBlock}>
            {formik.values.items.map((item, index) => (
              <div key={item.id} className={classes.inputBlock}>
                <Grid item xs={5}>
                  <SingleTextField
                    name={`items[${index}].soldToMaterialCode`}
                    value={item.soldToMaterialCode}
                    showClearIcon={true}
                    className={classes.inputTextField}
                    maxLengthProps={35}
                    onChange={event => {
                      handleChangeSoldToMaterialCode(event.target.value, index);
                    }}
                    placeholder={"ระบุ Customer Material"}
                    error={
                      formik.touched.items?.[index]?.soldToMaterialCode &&
                      Boolean(
                        (formik.errors.items?.[index] as any)
                          ?.soldToMaterialCode
                      )
                    }
                  />
                  {formik.touched.items?.[index]?.soldToMaterialCode &&
                    (formik.errors.items?.[index] as any)
                      ?.soldToMaterialCode && (
                      <MyTypography
                        variant="caption"
                        className={classes.validateError}
                      >
                        {
                          (formik.errors.items?.[index] as any)
                            ?.soldToMaterialCode
                        }
                      </MyTypography>
                    )}
                </Grid>
                <Grid item xs={isDeleteItem ? 6 : 7}>
                  <Autocomplete
                    resetAlways
                    optionsProp={materialSuggestions[item.id] || []}
                    placeholderProps={intl.formatMessage({
                      defaultMessage: "ค้นหา Material Code / Description",
                      description: "label"
                    })}
                    onChangeProps={(value: string) =>
                      handleChangeMatCodeKeyword(value, item.id)
                    }
                    onSearch={(value: string) =>
                      handleChangeMatCodeForIndex(value, index)
                    }
                    loading={loadingDataSuggestion}
                    id={`items[${index}].materialCode`}
                    name={`items[${index}].materialCode`}
                    error={
                      formik.touched.items?.[index]?.materialCode &&
                      Boolean(
                        (formik.errors.items?.[index] as any)?.materialCode
                      )
                    }
                  />
                  {formik.touched.items?.[index]?.materialCode &&
                    (formik.errors.items?.[index] as any)?.materialCode && (
                      <MyTypography
                        variant="caption"
                        className={classes.validateError}
                      >
                        {(formik.errors.items?.[index] as any)?.materialCode}
                      </MyTypography>
                    )}
                </Grid>
                {isDeleteItem && (
                  <Grid item xs={1} className={classes.deleteBox}>
                    <InlineSVG
                      src={minusCircle}
                      width={24}
                      height={24}
                      onClick={() => removeItem(index)}
                    />
                  </Grid>
                )}
              </div>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions disableSpacing={true}>
        <div>
          <MyTypography variant="subtitle3" className={classes.lengthItem}>
            <span>{formik.values.items.length || 1}/10</span>
          </MyTypography>
        </div>
        <div className={classes.btnAction}>
          <Button
            variant="outlined"
            className={classes.typeButton}
            onClick={() => addItem()}
            disabled={formik.values.items.length >= 10}
          >
            <InlineSVG
              src={plusIcon}
              width={24}
              height={24}
              className={classes.closeIcon}
              style={{ marginRight: 8 }}
            />
            <div className={classes.btnAdd}>เพิ่มรายการ</div>
          </Button>
          <Button
            variant="contained"
            className={`${classes.typeButton} ${classes.typeButtonSubmit}`}
            onClick={onSubmitForm}
            disabled={!isFormDisabled}
          >
            <InlineSVG
              src={saveIconWhite}
              width={16}
              height={16}
              className={classes.closeIcon}
              style={{ marginRight: 8 }}
            />
            <MyTypography variant="body1" className={classes.btnSubmit}>
              บันทึกข้อมูล
            </MyTypography>
          </Button>
        </div>
      </DialogActions>
      <div className={classes.footerActions}>
        <InlineSVG
          src={infoIcon}
          style={{ marginTop: 0 }}
          className={classes.infoIcon}
        />
        <FormattedMessage defaultMessage="กรุณากรอกข้อมูลที่จำเป็น (" />
        <span className={classes.required}>*</span>
        <FormattedMessage defaultMessage=")" />
      </div>
      {openProcessDialog && (
        <DialogWaitingConfirm
          open={openProcessDialog}
          onClose={() => {
            setOpenProcessDialog(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default DialogAddMaterial;
